import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import type { Lang } from "@/i18n";
import { path } from "@/lib/site";
import { Reveal } from "../Reveal";
import { Link } from "@tanstack/react-router";
import { useReferences } from "@/lib/usePublicContent";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";
import { Marquee } from "../Marquee";
import { MagneticButton } from "../MagneticButton";

interface RefItem {
  type: string;
  mission: string;
  duration: string;
  results: string[];
  tag: string;
}

export function ReferencesPage({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const fallback = t("references.items", { returnObjects: true }) as RefItem[];
  const { data: dbRefs } = useReferences(lang);
  const items: RefItem[] =
    dbRefs && dbRefs.length > 0
      ? dbRefs.map((r) => ({
          type: r.type,
          mission: r.mission,
          duration: r.duration,
          results: r.results,
          tag: r.tag,
        }))
      : fallback;

  const types = t("references.types", { returnObjects: true }) as string[];

  return (
    <>
      <Breadcrumbs lang={lang} current="references" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
              / References — {String(items.length).padStart(2, "0")}
            </span>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="lg:col-span-8">
              <h1 className="font-display font-black uppercase leading-[1.05] tracking-[-0.045em] text-foreground text-[clamp(2.5rem,8vw,7rem)]">
                <span>{t("references.h1")}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Brief
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">
                  {t("references.sub")}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TYPES — forWho-style bento chips */}
      {types?.length ? (
        <section className="section border-b-[1.5px] border-foreground">
          <div className="container-page">
            <Reveal>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                / Types
              </span>
              <h2 className="font-display mt-3 text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
                {t("references.typesTitle")}
              </h2>
            </Reveal>
            <ul className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {types.map((label, i) => {
                const accent = i % 5 === 2 ? "is-spark" : i % 7 === 4 ? "is-ink" : "";
                return (
                  <li key={label}>
                    <Reveal delay={i * 0.04}>
                      <div className={`bento-tile ${accent} flex items-center gap-4 px-6 py-6`}>
                        <span className="font-mono text-xs font-bold opacity-60">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[15px] font-medium">{label}</span>
                      </div>
                    </Reveal>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}

      {/* MARQUEE */}
      <Marquee
        variant="ink"
        items={[
          "Boutique hotels",
          "Aparthotels",
          "Hybrid hospitality",
          "Belgium · Luxembourg",
        ]}
      />

      {/* ENGAGEMENTS — Bento mosaic (1 large + N stacked) */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Engagements — {String(items.length).padStart(2, "0")}
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
              {t("references.sub")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3 md:gap-5">
            {items.map((it, i) => {
              const isFlagship = i === 0;
              return (
                <Reveal
                  key={i}
                  delay={i * 0.06}
                  className={isFlagship ? "md:col-span-2 md:row-span-2" : ""}
                >
                  <article
                    className={`bento-tile ${i === 0 ? "" : i === 1 ? "is-spark" : ""} tile-fx is-hover group flex h-full flex-col ${
                      isFlagship ? "p-8 md:p-12" : "p-7 md:p-8"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                        {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                      </span>
                      <span className="border-[1.5px] border-current px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em]">
                        {it.tag}
                      </span>
                    </div>
                    <h3
                      className={`font-display mt-8 font-black uppercase leading-[1] tracking-[-0.03em] ${
                        isFlagship ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"
                      }`}
                    >
                      {it.type}
                    </h3>
                    <p className="mt-4 text-[15px] font-medium opacity-90">{it.mission}</p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] opacity-60">
                      {it.duration}
                    </p>
                    <ul className="mt-auto pt-6 space-y-3">
                      {it.results.map((r, j) => (
                        <li
                          key={j}
                          className="flex gap-3 text-sm leading-relaxed opacity-90"
                        >
                          <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 bg-blaze" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section relative overflow-hidden bg-foreground text-background">
        <div className="container-page relative">
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <Reveal className="md:col-span-8">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
                / Contact
              </span>
              <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.92] tracking-[-0.04em] md:text-8xl">
                {t("references.cta")}
              </h2>
            </Reveal>
            <Reveal delay={0.08} className="md:col-span-4">
              <MagneticButton strength={0.4}>
                <Link
                  to={path(lang, "contact")}
                  className="btn-blaze inline-flex w-full items-center justify-between gap-2 px-6 py-4 text-sm font-bold uppercase tracking-[0.12em]"
                >
                  {t("common.bookSlot")} <ArrowUpRight size={18} />
                </Link>
              </MagneticButton>
            </Reveal>
          </div>
        </div>
      </section>
      <RelatedLinks lang={lang} items={["services", "about", "tools"]} />
    </>
  );
}
